import ArrowUp from '@assets/images/icons/arrow-up-light.svg?react'
import { NodeActionsToolbar } from '@components/ArtBoard/Node/components/NodeActionsToolbar'
import { NodePreviewWrapper } from '@components/ArtBoard/NodePreviewWrapper'
import { NOTE_TARGET, StickyNotes } from '@components/ArtBoard/StickyNotes'
import { AttachmentCollection } from '@components/ArtBoard/TabAttachments'
import { COMMENTABLE_TARGET, Threads } from '@components/ArtBoard/Threads'
import { Tooltip } from '@lib/components'
import { useClickOutside } from '@lib/hooks'
import {
  type Attachment,
  type Notes,
  selectNodeAttachments,
  selectNodeChildren,
  selectNodeParent,
} from '@octopus-do/octopus-project-core'
import { thunkAddArrow } from '@store/migrationActions/thunkActions/thunkArrowsActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { getFilteredTagsByNodeId } from '@store/reducers/tags/selector'
import { selectIsForceExpanded } from '@store/reducers/tags/visibility'
import { selectSeoPanelState } from '@storeOld/selectors'
import cx from 'classnames'
import type React from 'react'
import { useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import {
  NODE_GHOST,
  THEME_BOLD,
  TREE_DEFAULT,
  TREE_MATRIX,
} from '@/constants/constants'
import { useArtboardMode, usePrintRender } from '@/hooks'
import type { AppState, NodeFigma, TreeItemVariants } from '@/types'
import { isInsidePopover } from '@/utils'
import { hexToRgba } from '@/utils/colors'
import Blocks from '../Blocks/Blocks'
import { getDragAndDropAttributes } from '../DragAndDrop/getDragAndDropAttributes'
import useOnDragStart from '../DragAndDrop/hooks/useOnDragStart'
import { DRAGGABLE_TYPE, DROPZONE_TYPE } from '../DragAndDrop/types'
import { Children } from './components/Children'
import { ChildrenCorner } from './components/ChildrenCorner'
import { Estimates } from './components/Estimates'
import { GeneratePagesPopover } from './components/GeneratePagesPopover'
import { Handler } from './components/Handler'
import { Name } from './components/Name'
import { NodeTags } from './components/NodeTags'
import {
  NodeGallery,
  NodeGhost,
  NodeSeo,
  NodeSimpleView,
} from './components/NodeTypes'
import { ParentCorners } from './components/ParentCorners'
import { Siblings } from './components/Siblings'
import { VARIANTS } from './constants'
import { useOffscreenCulling, useSelection } from './hooks'
import useActions from './hooks/useActions'
import css from './Node.module.scss'
import type { NodeClasses } from './types'

type Props = {
  arrowMode: boolean
  id: string
  isCollapsed: boolean
  url: string
  isChild: boolean
  readOnly?: boolean
  image: {
    id: string
    imageUrl: string
    thumbnailUrl: string
  } | null
  loading?: boolean
  isOverlay?: boolean
  variant: TreeItemVariants
  oldVariant?: TreeItemVariants
  isDraggable: boolean
  notes: Notes
  figma: NodeFigma | null
  onImageClick?: (nodeId: string) => void
}

const Node: React.FC<Props> = ({
  id,
  isDraggable = true,
  isOverlay = false,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  // const estimates = useEstimates()
  const selection = useSelection(id)

  const onDragStart = useOnDragStart(ref, DRAGGABLE_TYPE.PAGE, id, {})

  const isProjectMobile = useSelector(
    migrationProjectSelector.getIsProjectMobile
  )

  const isProjectNeutral = useSelector(
    migrationProjectSelector.getIsProjectNeutral
  )

  const {
    title,
    colorId,
    collapsed: isCollapsedInProject,
  } = useSelector(migrationProjectSelector.getProjectNodeById(id))

  // Page-tag filter (OCT-5007): a collapsed node holding a match is expanded
  // for the duration of the filter, so it must render its children instead of
  // the collapsed preview. The project's `collapsed` flag is left untouched.
  const isForceExpanded = useSelector(selectIsForceExpanded(id))

  const collapsed = isCollapsedInProject && !isForceExpanded

  const colorHexUI = useSelector(
    migrationProjectSelector.getProjectColorForUI(colorId)
  )

  const { onCollapse } = useActions(id)

  const { isPrinting } = usePrintRender()

  const treeMode = useSelector(migrationProjectSelector.getProjectTreeType)

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

  const ids = useSelector<AppState, string[]>(({ artboard }) => {
    if (!isOverlay) {
      return []
    }

    return artboard.dragAndDrop.dragged.ids
  }, shallowEqual)

  const {
    arrows: isArrowMode,
    symbolLibrary: isSymbolMode,
    simpleView,
    gallery,
    seoMode,
    estimate,
    readonlyEstimate,
  } = useArtboardMode()

  // the card element exists only in the default tree view; other modes render their own node
  useOffscreenCulling(
    ref,
    css.offscreen,
    !isOverlay && !isPrinting && !simpleView && !gallery && !seoMode
  )

  const showEstimate = estimate || readonlyEstimate

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )
  const artboardDisabled = useSelector(
    ({ artboard }: AppState) => artboard.isDisabled
  )

  const isLastSelected = useSelector<AppState, boolean>(
    ({ canvas }) =>
      !!id &&
      canvas.node.selectedNodes[canvas.node.selectedNodes.length - 1] === id
  )

  // The title input may already have focus when the artboard goes off.
  const isEditable = useSelector<AppState, boolean>(
    ({ canvas }) => !artboardDisabled && canvas.node.selectedToEditNode === id
  )

  const isSelected = useSelector<AppState, boolean>(({ canvas }) =>
    canvas.node.selectedNodes.includes(id)
  )

  const isDragging = useSelector<AppState, boolean>(
    ({ artboard }) =>
      !isOverlay &&
      (id === artboard.dragAndDrop.dragged.id ||
        artboard.dragAndDrop.dragged.ids.includes(id)) &&
      artboard.dragAndDrop.isActive
  )

  // a page dragged over this one, or a thread that would attach here
  const isOver = useSelector<AppState, boolean>(
    ({ artboard }) =>
      (id === artboard.dragAndDrop.dropZone.id &&
        id !== artboard.dragAndDrop.dragged.id) ||
      id === artboard.threadTarget
  )

  const attachments = useSelector<AppState, Attachment[]>(
    (state) =>
      selectNodeAttachments(id)({
        project: state.manager.project,
        relations: state.relations,
      }),
    shallowEqual
  )

  const theme = useSelector(migrationProjectSelector.getProjectTheme)

  const { filteredByTag, filterIsActive } = useSelector<
    AppState,
    { filteredByTag: boolean; filterIsActive: boolean }
  >((state) => getFilteredTagsByNodeId(state, id), shallowEqual)

  const showSeoNodeId = useSelector(selectSeoPanelState)

  // @todo implement later
  // const previewColor = useSelector<AppState, string | null>(
  //   (state) => getPreviewColor(state, id),
  //   shallowEqual
  // )

  const childIds = useSelector(selectNodeChildren(id), shallowEqual)
  const parentIds = useSelector(selectNodeParent(id), shallowEqual)
  const parentId = parentIds[0] || null

  const canDrag =
    !isReadOnly && !isSymbolMode && isDraggable && (!isEditable || seoMode)
  const isChild = parentId !== null
  const hasChild = childIds.length === 1
  const hasChildren = childIds.length > 1
  // const hasHours =
  //   estimates.byNode[id] &&
  //   estimates.byNode[id].filter((id) => !(estimates.byId[id].hours > 0))
  //     .length === 0
  const hasHours = false

  const onClickOut = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!e.shiftKey && !isInsidePopover(e.target)) {
      selection.clearSelection()
    }
  }

  const cursorMode = useSelector((store: AppState) => store.artboard.cursorMode)

  const clickOutside = useClickOutside<HTMLDivElement>(
    isLastSelected && cursorMode === 'grab',
    onClickOut
  )

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // a note hanging on the page is drawn inside it, but is not the page
    if ((e.target as Element).closest('[data-sticky-note]')) {
      return
    }

    if (isArrowMode) {
      e.stopPropagation()
      e.preventDefault()
      dispatch(thunkAddArrow(id, 'node'))
    }
  }

  const nodeStyles = {
    '--node-color': colorHexUI,
    '--node-select-color': hexToRgba(colorHexUI, 0.25),
  } as React.CSSProperties

  const nodeClasses: NodeClasses = {
    container: cx('Tree-node', css.container, {
      [css.selected]: isSelected,
      [css.isChild]: isChild,
      [css.hasHours]: hasHours,
      [css.ghost]: props.variant === 'ghost',
      [css.mobile]: isProjectMobile,
      [css.map]: treeMode === TREE_DEFAULT && !isOverlay,
      [css.dragging]: isDragging,
      [css.opacity]: filterIsActive && !filteredByTag,
      [css.cancelOpacity]: !isReadOnly && !isOverlay && isLastSelected,
      [css.isSafari]: isSafari,
    }),
    node: cx('Tree-node-inner', css.node, {
      'node--mobile': isProjectMobile,

      [css.active]: isSelected || showSeoNodeId === id,
      [css.mobile]: isProjectMobile,
      [css.over]: isOver,

      // variants
      [css.page]: props.variant === VARIANTS.DEFAULT,
      [css.dashed]: props.variant === VARIANTS.FRAME,
      [css.stack]: props.variant === VARIANTS.STACK,
      [css.ghost]: props.variant === VARIANTS.GHOST,
      [css.theme_bold]: theme === THEME_BOLD,

      [css.exporting]: isPrinting,
    }),
    body: css.body,
    stackGhost: cx(css.stackGhost, 'stack-ghost'),
  }

  if (simpleView) {
    return (
      <NodeSimpleView
        id={id}
        name={title}
        variant={props.variant}
        isChild={isChild}
        hasChild={hasChild}
        hasChildren={hasChildren}
        parentId={parentId}
        isCollapsed={collapsed}
        nodeStyles={nodeStyles}
        canDrag={canDrag && !isPrinting}
        isEditable={isEditable}
        isSelected={isSelected}
        isOver={isOver}
        isDragging={isDragging}
        isOverlay={isOverlay}
        clickOutside={clickOutside}
        creationDisabled={
          isReadOnly ||
          artboardDisabled ||
          !!props.readOnly ||
          isOverlay ||
          isDragging ||
          isEditable ||
          isPrinting
        }
      />
    )
  }

  if (gallery) {
    return (
      <NodeGallery
        id={id}
        name={title}
        color={colorHexUI}
        variant={props.variant}
        image={props.image}
        url={props.url}
        nodeClasses={nodeClasses}
        readOnly={props.readOnly || false}
        isChild={isChild}
        hasChild={hasChild}
        hasChildren={hasChildren}
        isCollapsed={collapsed}
        parentId={parentId}
        figma={props.figma}
      />
    )
  }

  if (seoMode) {
    return (
      <NodeSeo
        id={id}
        title={title}
        variant={props.variant}
        isChild={isChild}
        hasChild={hasChild}
        hasChildren={hasChildren}
        isCollapsed={collapsed}
        parentId={parentId}
        notes={props.notes}
        url={props.url}
        nodeClasses={nodeClasses}
        nodeStyles={nodeStyles}
        color={colorHexUI}
        canDrag={canDrag && !isPrinting}
        isOverlay={isOverlay}
        creationDisabled={
          isReadOnly ||
          artboardDisabled ||
          !!props.readOnly ||
          isOverlay ||
          isDragging ||
          isEditable ||
          isPrinting
        }
      />
    )
  }

  return (
    <div
      {...{ [COMMENTABLE_TARGET]: id }}
      {...(!isOverlay && { [NOTE_TARGET]: id })}
      className={nodeClasses.container}
      style={nodeStyles}
      id={id}
      data-scroll-id={id}
      ref={ref}
      onPointerDown={onClick}
      {...getDragAndDropAttributes(
        id,
        DROPZONE_TYPE.PAGE,
        {
          nodeId: id,
          // @TODO review this and fix
          // @ts-expect-error - Property 'group' does not exist on type
          group: props.group,
        },
        isOverlay
      )}
    >
      <Threads target={id} anchored />
      {!isOverlay && !seoMode && <StickyNotes parentId={id} anchor="page" />}
      <AttachmentCollection attachments={attachments} anchored />
      {isChild && treeMode !== TREE_MATRIX && !isOverlay && (
        <ChildrenCorner id={id} parentId={parentId} />
      )}

      {(hasChildren || hasChild) &&
        treeMode !== TREE_MATRIX &&
        !collapsed &&
        !isOverlay && <ParentCorners />}

      {props.variant === VARIANTS.STACK && (
        <div className={nodeClasses.stackGhost} />
      )}

      <div className={nodeClasses.node} {...(isSelected ? clickOutside : {})}>
        <div>
          {isOverlay && ids.length > 1 && (
            <div className={css.numberOfPages}>{ids.length} pages</div>
          )}

          {!isReadOnly && !isOverlay && isLastSelected && (
            <NodeActionsToolbar
              colorId={colorId}
              nodeId={id}
              variant={props.variant}
              url={props.url}
            />
          )}

          {props.variant === NODE_GHOST ? (
            <NodeGhost
              id={id}
              isOverlay={isOverlay}
              canDrag={canDrag}
              containerRef={ref}
            />
          ) : (
            <>
              <div
                onPointerDown={
                  canDrag && !isArrowMode ? onDragStart : undefined
                }
              >
                {(props.variant === VARIANTS.DEFAULT ||
                  props.variant === VARIANTS.STACK) &&
                  !isProjectNeutral && (
                    <Handler
                      canDrag={canDrag}
                      isSelected={isSelected}
                      variant={props.variant}
                    />
                  )}

                <Name
                  id={id}
                  name={title}
                  isEditable={isEditable}
                  variant={props.variant}
                />
              </div>

              <div className={nodeClasses.body}>
                <Blocks nodeId={id} />
              </div>
            </>
          )}

          {!isOverlay && !isDragging && (
            <div className={cx(css.actions, 'node-actions')}>
              {!isEditable && (
                <div className={css.siblings}>
                  <Siblings
                    nodeId={id}
                    url={props.url}
                    variant={props.variant}
                    readOnly={isReadOnly}
                  />
                </div>
              )}

              {!isEditable && !isReadOnly && !collapsed && (
                <div className={css.children}>
                  <Children nodeId={id} />
                </div>
              )}

              {!isEditable && !isReadOnly && !collapsed && (
                <div className={css.children}>
                  <GeneratePagesPopover nodeId={id} />
                </div>
              )}

              {childIds.length > 0 && !collapsed && (
                <Tooltip
                  content="Collapse"
                  position="top"
                  onClick={onCollapse}
                  className={cx(css.collapse, css.actionButton, {
                    [css.isCollapsed]: collapsed,
                    [css.ghost]: props.variant === 'ghost',
                  })}
                >
                  <ArrowUp />
                </Tooltip>
              )}

              <NodeTags id={id} />
            </div>
          )}

          {!isOverlay && !isDragging && showEstimate && (
            <Estimates nodeId={id} />
          )}

          {collapsed && <NodePreviewWrapper nodeId={id} />}
        </div>
      </div>
      <div
        className={css.droppable}
        {...getDragAndDropAttributes(
          id,
          DROPZONE_TYPE.PAGE,
          {
            nodeId: id,
            // @TODO review this and fix
            // @ts-expect-error - Property 'group' does not exist on type
            group: props.group,
          },
          isOverlay
        )}
      />
    </div>
  )
}

export default Node
